import { Route, Routes, BrowserRouter, Navigate } from 'react-router-dom';
import { ThemeProvider } from 'styled-components';

import './App.css';
import RootLayout from './Components/layout/RootLayout';
import Payments from './modules/Pages/Payments/Payments';
import SignOut from './modules/Pages/SignOut/SignOut';
import Leave from './modules/Pages/Leave/Leave';
import GlobalPlanAddon from './modules/Pages/GlobalPlanAddon/GlobalPlanAddon';
import { AuthProvider } from './hooks/AuthContext';
import { SecureComponent } from './Components/SecureComponent';
import { QueryClient, QueryClientProvider, useQuery } from 'react-query';
import User from './services/bioplus-services/user';
import { CompanyProvider } from './hooks/CompanyContext';
import { UserProvider, useUserContext } from './hooks/UserContext';
import AcceptCompanyInvitation from './modules/Pages/Company/accept-company-invitation';
import UserInvitation from './modules/Pages/UserInvitation/userInvitation';
import { AppHolder, ThemedGlobalStyle, light } from './services/settings';
import {
  CorporateDashboard,
  Dashboard,
  EmployeeDashboard,
  PartnerDashboard,
  VisitorDashboard,
} from './modules/Pages/DashboardV2';
import {
  AdminRoles,
  AdminUsers,
  CorporateAdminUsers,
  EmployeeUser,
  ManagerRoles,
  ManagerUser,
  OwnerRoles,
  PartnerUsers,
  Users,
} from './modules/Pages/UserV2';
import { CorporateCompany, OwnerCompany, PartnerCompanies } from './modules/Pages/CompanyV2';
import {
  CorporateAdminPremise,
  EmployeePremises,
  ManagerPremise,
  PartnerPremises,
  Premises,
} from './modules/Pages/PremiseV2';
import {
  AdminGeofence,
  CorporateGeofence,
  EmployeeGeofence,
  GeofenceMap,
  ManagerGeofence,
  OwnerGeofence,
  PartnerGeofence,
} from './modules/Pages/GeofenceV2';
import { Attendance, ManagerAttendance, VisitorAttendance } from './modules/Pages/AttendanceV2';
import { ManagerNotification, Notifications } from './modules/Pages/NotificationV2';
import { SOS } from './modules/Pages/SOSV2';
import {
  AdminLogRecords,
  // AdminLogRecords,
  CorporateLogRecords,
  EmployeeLogRecords,
  LogRecords,
  ManagerLogRecords,
  PartnerLogRecords,
  VisitorLogRecords,
} from './modules/Pages/LogRecordsV2';
import { PartnerSubscriptionPlan, SubscriptionPlan } from './modules/Pages/SubscriptionPlanV2';
import { Cvd, EmployeeCvd, ManagerCvd } from './modules/Pages/CvdV2';
import Login from './modules/Pages/LoginV2';
import { IntlProvider } from 'react-intl';
import EnglishContent from './lang/english.json';
import PortugueseContent from './lang/portuguese.json';
import { LanguageProvider, useLanguage } from './hooks/useLanguageSwitch';
import { ManagerBroadcast, OwnerBroadcast, PartnerBroadcast } from './modules/Pages/Broadcast';
import AcceptUserInvitation from './modules/Pages/UserInvitation/acceptUserInvitation';
import UserInvitationV2 from './modules/Pages/UserInvitation/userInvitationV2';
import { AddressBook } from './modules/Pages/AddressBook';
import { SubscriptionAddOn } from './modules/Pages/SubscriptionAddOn';
import ManagerDashboard from './modules/Pages/DashboardV2/managerDashboard';
import Invoice from './modules/Pages/publicInvoiceView';
import { Library } from './modules/Pages/library';
import AdminDashboard from './modules/Pages/DashboardV2/adminDashboard';
import AdminPremises from './modules/Pages/PremiseV2/adminPremise';
import AdminCompanies from './modules/Pages/CompanyV2/adminCompanies';
import AdminAttendance from './modules/Pages/AttendanceV2/AdminAttendance';
import PartnerAttendance from './modules/Pages/AttendanceV2/partnerAttendance';
import PartnerCoupon from './modules/Pages/DiscountCoupon/partnerCoupon';
import { FriendList } from './modules/Pages/FriendFeature';
import { Envd, ENVDForm } from './modules/Pages/Envd';
import { LoadingText, LoadingWrapper } from './services/settings/appStyle/global.style';
import Spinner from './Components/uielements/loader/spinner';
import EmailTemplateList from './modules/Pages/EmailTemplate';
import CheckList from './modules/Pages/checkList';
import { DeclarationList } from './modules/Pages/declarationList';
import PartnerRoles from './modules/Pages/UserV2/partnerRoles';
import { AdminPartnerCustomRoles, OwnerCustomRoles } from './modules/Pages/CustomRoles';
import AdminUserManagerial from './modules/Pages/Managerial/AdminUserManagerial';
import DistributorUserManagerial from './modules/Pages/Managerial/DistributorUserManagerial';

function getObjectWithPermission(array) {
  for (let obj of array) {
    if (obj.hasOwnProperty('permission')) {
      return obj;
    }
  }
  return null;
}

// ── Route → permission-module label mapping ──────────────────────────────
// Add/adjust entries here as new guarded routes are introduced. Only routes
// listed here are affected — any route not present is left completely
// untouched, same as before.
const ROUTE_PERMISSION_MAP = {
  '/users': 'user',
  '/premises': 'premise',
  '/geofences': 'geofence',
  '/geofenceMap': 'geofence',
  '/logrecords': 'logbook',
  '/attendance': 'timesheet',
  '/sos': 'sos',
  // '/broadcast': 'broadcast',
  '/cvd': 'edec',
  '/declarationList': 'declaration',
  '/checkList': 'checklist',
  '/distributors': 'distributor',
};

// ── ModuleGuard ────────────────────────────────────────────────────────────
// Wraps a route's element. If the matched permission module exists and its
// isView flag is explicitly false, redirects to the dashboard index ("/")
// instead of rendering the page. This protects against a user typing the
// URL directly (not just hiding the nav link), while still rendering
// normally when there's no permission data yet or no mapping for the path.
const ModuleGuard = ({ path, children }) => {
  const userContext = useUserContext();
  const { userPermission } = userContext ?? {};

  const label = ROUTE_PERMISSION_MAP[path];

  if (!label) return children; // no permission mapping for this route — behavior unchanged

  const modulePermission = userPermission?.find((permission) => permission?.label === label);

  if (modulePermission && modulePermission?.isView === false) {
    return <Navigate to="/" replace />;
  }

  return children;
};

// const AdminRouter = () => {
//   return (
//     <Routes>
//       <Route path="/" element={<RootLayout />}>
//         <Route index element={<Dashboard />} />
//         <Route path="/users" element={<AdminUsers />} />
//         <Route path="/companies" element={<AdminCompanyList />} />
//         <Route path="/companies/:companyId" element={<ViewCompanyDialog />} />
//         <Route path="/companies/:companyId/plan" element={<ChangePlanDialog />} />
//         {/* <Route path="/geofences" element={<AdminGeofences />} /> */}
//         <Route path="/geofenceMap" element={<GeofenceMap />} />
//         <Route path="/logrecords" element={<AdminLogRecords />} />
//         <Route path="/notifications" element={<Notifications />} />
//         <Route path="/attendance" element={<Attendance />} />
//         <Route path="/leave" element={<Leave />} />
//         <Route path="/sos" element={<SOS />} />
//         <Route path="/payments" element={<Payments />} />
//         <Route path="/signout" element={<SignOut />} />
//         <Route path="/cvd" element={<Cvd />} />
//         <Route path="/envd" element={<Envd />} />
//         <Route path="/subscription" element={<SubscriptionPlan />} />
//         <Route path="/globalPlanAddon" element={<GlobalPlanAddon />} />
//         <Route path="/user-invitations/:invitationId" element={<AcceptUserInvitation />} />
//         <Route path="/*" element={<Dashboard />} />
//       </Route>
//     </Routes>
//   );
// };

const AdminRouter = () => {
  return (
    <Routes>
      <Route path="/" element={<RootLayout />}>
        <Route index element={<AdminDashboard />} />
        <Route
          path="/users"
          element={
            <ModuleGuard path="/users">
              <AdminUsers />
            </ModuleGuard>
          }
        />
        <Route path="/roles" element={<AdminRoles />} />
        <Route path="/companies" element={<AdminCompanies />} />
        <Route
          path="/geofences"
          element={
            <ModuleGuard path="/geofences">
              <AdminGeofence />
            </ModuleGuard>
          }
        />
        <Route
          path="/geofenceMap"
          element={
            <ModuleGuard path="/geofenceMap">
              <GeofenceMap />
            </ModuleGuard>
          }
        />
        <Route
          path="/logrecords"
          element={
            <ModuleGuard path="/logrecords">
              <AdminLogRecords />
            </ModuleGuard>
          }
        />
        <Route path="/notifications" element={<Notifications />} />
        <Route
          path="/attendance"
          element={
            <ModuleGuard path="/attendance">
              <AdminAttendance />
            </ModuleGuard>
          }
        />
        <Route path="/leave" element={<Leave />} />
        <Route
          path="/sos"
          element={
            <ModuleGuard path="/sos">
              <SOS />
            </ModuleGuard>
          }
        />
        <Route path="/payments" element={<Payments />} />
        <Route
          path="/premises"
          element={
            <ModuleGuard path="/premises">
              <AdminPremises />
            </ModuleGuard>
          }
        />
        <Route path="/signout" element={<SignOut />} />
        <Route path="/cvd" element={<Cvd />} />
        <Route path="/subscription" element={<SubscriptionPlan />} />
        <Route path="/globalPlanAddon" element={<GlobalPlanAddon />} />
        <Route
          path="/broadcast"
          element={
            <ModuleGuard path="/broadcast">
              <PartnerBroadcast />
            </ModuleGuard>
          }
        />
        <Route path="/*" element={<Dashboard />} />
        <Route path="/user-invitations/:invitationId" element={<AcceptUserInvitation />} />
        {/* Invitation link — UserInvitationV2 handles its own session check internally */}
        <Route path="/auth/invitations/:invitationId" element={<UserInvitationV2 />} />
        <Route path="/friends" element={<FriendList />} />
        <Route path="/emailTemplate" element={<EmailTemplateList />} />{' '}
        <Route path="/declarationList" element={<DeclarationList />} />
        <Route path="/admins" element={<AdminUserManagerial />} />
        <Route path="/distributors" element={<DistributorUserManagerial />} />{' '}
        <Route path="/checkList" element={<CheckList />} />
        <Route path="/customRoles" element={<AdminPartnerCustomRoles />} />
      </Route>
    </Routes>
  );
};

const PartnerRouter = () => {
  return (
    <Routes>
      <Route path="/" element={<RootLayout />}>
        <Route index element={<PartnerDashboard />} />
        <Route
          path="/users"
          element={
            <ModuleGuard path="/users">
              <PartnerUsers />
            </ModuleGuard>
          }
        />
        <Route path="/roles" element={<PartnerRoles />} />
        <Route path="/companies" element={<PartnerCompanies />} />
        <Route
          path="/geofences"
          element={
            <ModuleGuard path="/geofences">
              <PartnerGeofence />
            </ModuleGuard>
          }
        />
        <Route
          path="/geofenceMap"
          element={
            <ModuleGuard path="/geofenceMap">
              <GeofenceMap />
            </ModuleGuard>
          }
        />
        <Route
          path="/logrecords"
          element={
            <ModuleGuard path="/logrecords">
              <PartnerLogRecords />
            </ModuleGuard>
          }
        />
        <Route path="/notifications" element={<Notifications />} />
        <Route
          path="/attendance"
          element={
            <ModuleGuard path="/attendance">
              <PartnerAttendance />
            </ModuleGuard>
          }
        />
        <Route path="/leave" element={<Leave />} />
        <Route
          path="/sos"
          element={
            <ModuleGuard path="/sos">
              <SOS />
            </ModuleGuard>
          }
        />
        <Route path="/payments" element={<Payments />} />
        <Route
          path="/premises"
          element={
            <ModuleGuard path="/premises">
              <PartnerPremises />
            </ModuleGuard>
          }
        />
        <Route path="/signout" element={<SignOut />} />
        <Route path="/cvd" element={<Cvd />} />
        <Route path="/subscription" element={<PartnerSubscriptionPlan />} />
        <Route path="/globalPlanAddon" element={<GlobalPlanAddon />} />
        <Route
          path="/broadcast"
          element={
            <ModuleGuard path="/broadcast">
              <PartnerBroadcast />
            </ModuleGuard>
          }
        />
        <Route path="/coupon" element={<PartnerCoupon />} />
        <Route path="/*" element={<Dashboard />} />
        <Route path="/user-invitations/:invitationId" element={<AcceptUserInvitation />} />
        {/* Invitation link — UserInvitationV2 handles its own session check internally */}
        <Route path="/auth/invitations/:invitationId" element={<UserInvitationV2 />} />
        <Route path="/friends" element={<FriendList />} />
        <Route path="/distributors" element={<DistributorUserManagerial />} />{' '}
        <Route path="/checkList" element={<CheckList />} />
        <Route path="/declarationList" element={<DeclarationList />} />
        <Route path="/customRoles" element={<AdminPartnerCustomRoles />} />
      </Route>
    </Routes>
  );
};

const OwnerRouter = () => {
  return (
    <Routes>
      <Route path="/" element={<RootLayout />}>
        <Route index element={<Dashboard />} />
        <Route path="/update-profile" element={<OwnerCompany />} />
        <Route
          path="/users"
          element={
            <ModuleGuard path="/users">
              <Users />
            </ModuleGuard>
          }
        />
        <Route path="/roles" element={<OwnerRoles />} />
        <Route
          path="/geofences"
          element={
            <ModuleGuard path="/geofences">
              <OwnerGeofence />
            </ModuleGuard>
          }
        />
        <Route
          path="/geofenceMap"
          element={
            <ModuleGuard path="/geofenceMap">
              <GeofenceMap />
            </ModuleGuard>
          }
        />
        <Route
          path="/logrecords"
          element={
            <ModuleGuard path="/logrecords">
              <LogRecords />
            </ModuleGuard>
          }
        />
        <Route path="/notifications" element={<Notifications />} />
        <Route
          path="/attendance"
          element={
            <ModuleGuard path="/attendance">
              <Attendance />
            </ModuleGuard>
          }
        />
        <Route path="/leave" element={<Leave />} />
        <Route
          path="/premises"
          element={
            <ModuleGuard path="/premises">
              <Premises />
            </ModuleGuard>
          }
        />
        <Route
          path="/sos"
          element={
            <ModuleGuard path="/sos">
              <SOS />
            </ModuleGuard>
          }
        />
        <Route path="/signout" element={<SignOut />} />
        <Route path="/cvd" element={<Cvd />} />
        <Route path="/envd" element={<Envd />} />
        <Route path="/envd/form" element={<ENVDForm />} />
        <Route
          path="/broadcast"
          element={
            <ModuleGuard path="/broadcast">
              <OwnerBroadcast />
            </ModuleGuard>
          }
        />
        <Route path="/addressBook" element={<AddressBook />} />{' '}
        <Route path="/addOn" element={<SubscriptionAddOn />} />
        <Route path="/library" element={<Library />} />
        <Route path="/friends" element={<FriendList />} />
        <Route path="/*" element={<Dashboard />} />
        <Route path="/user-invitations/:invitationId" element={<AcceptUserInvitation />} />{' '}
        {/* Invitation link — UserInvitationV2 handles its own session check internally */}
        <Route path="/auth/invitations/:invitationId" element={<UserInvitationV2 />} />
        <Route path="/checkList" element={<CheckList />} />
        <Route path="/declarationList" element={<DeclarationList />} />
        <Route path="/customRoles" element={<OwnerCustomRoles />} />
      </Route>
    </Routes>
  );
};

const DefaultRoleRoutes = () => {
  return (
    <Routes>
      <Route path="/" element={<RootLayout />}>
        <Route index element={<Dashboard />} />
        <Route path="/update-profile" element={<OwnerCompany />} />
        <Route
          path="/users"
          element={
            <ModuleGuard path="/users">
              <Users />
            </ModuleGuard>
          }
        />
        <Route path="/roles" element={<OwnerRoles />} />
        <Route
          path="/geofences"
          element={
            <ModuleGuard path="/geofences">
              <OwnerGeofence />
            </ModuleGuard>
          }
        />
        <Route
          path="/geofenceMap"
          element={
            <ModuleGuard path="/geofenceMap">
              <GeofenceMap />
            </ModuleGuard>
          }
        />
        <Route
          path="/logrecords"
          element={
            <ModuleGuard path="/logrecords">
              <LogRecords />
            </ModuleGuard>
          }
        />
        <Route path="/notifications" element={<Notifications />} />
        <Route
          path="/attendance"
          element={
            <ModuleGuard path="/attendance">
              <Attendance />
            </ModuleGuard>
          }
        />
        <Route path="/leave" element={<Leave />} />
        <Route
          path="/premises"
          element={
            <ModuleGuard path="/premises">
              <Premises />
            </ModuleGuard>
          }
        />
        <Route
          path="/sos"
          element={
            <ModuleGuard path="/sos">
              <SOS />
            </ModuleGuard>
          }
        />
        <Route path="/signout" element={<SignOut />} />
        <Route path="/cvd" element={<Cvd />} />
        <Route path="/envd" element={<Envd />} />
        <Route path="/envd/form" element={<ENVDForm />} />
        <Route
          path="/broadcast"
          element={
            <ModuleGuard path="/broadcast">
              <OwnerBroadcast />
            </ModuleGuard>
          }
        />
        <Route path="/addressBook" element={<AddressBook />} />{' '}
        <Route path="/addOn" element={<SubscriptionAddOn />} />
        <Route path="/library" element={<Library />} />
        <Route path="/friends" element={<FriendList />} />
        <Route path="/*" element={<Dashboard />} />
        <Route path="/user-invitations/:invitationId" element={<AcceptUserInvitation />} />{' '}
        {/* Invitation link — UserInvitationV2 handles its own session check internally */}
        <Route path="/auth/invitations/:invitationId" element={<UserInvitationV2 />} />
        <Route path="/checkList" element={<CheckList />} />
        <Route path="/declarationList" element={<DeclarationList />} />
      </Route>
    </Routes>
  );
};

const VisitorRouter = () => {
  return (
    <Routes>
      <Route path="/" element={<RootLayout />}>
        <Route index element={<VisitorDashboard />} />
        <Route
          path="/logrecords"
          element={
            <ModuleGuard path="/logrecords">
              <VisitorLogRecords />
            </ModuleGuard>
          }
        />
        <Route
          path="/attendance"
          element={
            <ModuleGuard path="/attendance">
              <VisitorAttendance />
            </ModuleGuard>
          }
        />
        <Route
          path="/geofences"
          element={
            <ModuleGuard path="/geofences">
              <OwnerGeofence />
            </ModuleGuard>
          }
        />
        <Route
          path="/geofenceMap"
          element={
            <ModuleGuard path="/geofenceMap">
              <GeofenceMap />
            </ModuleGuard>
          }
        />
        <Route path="/friends" element={<FriendList />} />
        {/* Invitation link — UserInvitationV2 handles its own session check internally */}
        <Route path="/auth/invitations/:invitationId" element={<UserInvitationV2 />} />
      </Route>
    </Routes>
  );
};

const ManagerRouter = () => {
  return (
    <Routes>
      <Route path="/" element={<RootLayout />}>
        <Route index element={<ManagerDashboard />} />
        <Route
          path="/users"
          element={
            <ModuleGuard path="/users">
              <ManagerUser />
            </ModuleGuard>
          }
        />
        <Route path="/roles" element={<ManagerRoles />} />
        <Route
          path="/geofences"
          element={
            <ModuleGuard path="/geofences">
              <ManagerGeofence />
            </ModuleGuard>
          }
        />
        <Route
          path="/geofenceMap"
          element={
            <ModuleGuard path="/geofenceMap">
              <GeofenceMap />
            </ModuleGuard>
          }
        />
        <Route
          path="/logrecords"
          element={
            <ModuleGuard path="/logrecords">
              <ManagerLogRecords />
            </ModuleGuard>
          }
        />
        <Route path="/notifications" element={<ManagerNotification />} />
        <Route
          path="/premises"
          element={
            <ModuleGuard path="/premises">
              <ManagerPremise />
            </ModuleGuard>
          }
        />
        <Route
          path="/attendance"
          element={
            <ModuleGuard path="/attendance">
              <ManagerAttendance />
            </ModuleGuard>
          }
        />
        <Route path="/leave" element={<Leave />} />
        <Route
          path="/sos"
          element={
            <ModuleGuard path="/sos">
              <SOS />
            </ModuleGuard>
          }
        />
        <Route path="/signout" element={<SignOut />} />
        <Route
          path="/broadcast"
          element={
            <ModuleGuard path="/broadcast">
              <ManagerBroadcast />
            </ModuleGuard>
          }
        />
        <Route path="/cvd" element={<ManagerCvd />} />
        <Route path="/user-invitations/:invitationId" element={<AcceptUserInvitation />} />
        {/* Invitation link — UserInvitationV2 handles its own session check internally */}
        <Route path="/auth/invitations/:invitationId" element={<UserInvitationV2 />} />
        <Route path="/*" element={<Dashboard />} />
        <Route path="/friends" element={<FriendList />} />
        <Route path="/update-profile" element={<OwnerCompany />} />
      </Route>
    </Routes>
  );
};

const CorporateRouter = () => {
  return (
    <Routes>
      <Route path="/" element={<RootLayout />}>
        <Route
          path="/users"
          element={
            <ModuleGuard path="/users">
              <CorporateAdminUsers />
            </ModuleGuard>
          }
        />
        <Route path="/companies" element={<CorporateCompany />} />
        <Route
          path="/premises"
          element={
            <ModuleGuard path="/premises">
              <CorporateAdminPremise />
            </ModuleGuard>
          }
        />
        <Route
          path="/geofences"
          element={
            <ModuleGuard path="/geofences">
              <CorporateGeofence />
            </ModuleGuard>
          }
        />
        <Route
          path="/geofenceMap"
          element={
            <ModuleGuard path="/geofenceMap">
              <GeofenceMap />
            </ModuleGuard>
          }
        />
        <Route
          path="/logrecords"
          element={
            <ModuleGuard path="/logrecords">
              <CorporateLogRecords />
            </ModuleGuard>
          }
        />
        <Route path="/notifications" element={<Notifications />} />
        <Route
          path="/attendance"
          element={
            <ModuleGuard path="/attendance">
              <Attendance />
            </ModuleGuard>
          }
        />
        <Route path="/leave" element={<Leave />} />
        <Route
          path="/sos"
          element={
            <ModuleGuard path="/sos">
              <SOS />
            </ModuleGuard>
          }
        />
        <Route path="/signout" element={<SignOut />} />
        <Route path="/cvd" element={<Cvd />} />
        <Route path="/invitations/:invitationId" element={<AcceptCompanyInvitation />} />
        {/* Invitation link — UserInvitationV2 handles its own session check internally */}
        <Route path="/auth/invitations/:invitationId" element={<UserInvitationV2 />} />
        <Route path="/*" element={<CorporateDashboard />} />
        <Route index element={<CorporateDashboard />} />
        <Route path="/friends" element={<FriendList />} />
      </Route>
    </Routes>
  );
};

// const EmployeeRouter = () => {
//   return (
//     <Routes>
//       {/* <Route path="/" element={<RootLayout />}>
//         <Route index element={<Dashboard />} />
//         <Route path="/signout" element={<SignOut />} />
//       </Route> */}
//       <Route path="/" element={<RootLayout />}>
//         <Route index element={<EmployeeDashboard />} />
//         <Route
//           path="/users"
//           element={
//             <ModuleGuard path="/users">
//               <EmployeeUser />
//             </ModuleGuard>
//           }
//         />
//         <Route
//           path="/geofences"
//           element={
//             <ModuleGuard path="/geofences">
//               <EmployeeGeofence />
//             </ModuleGuard>
//           }
//         />
//         <Route
//           path="/geofenceMap"
//           element={
//             <ModuleGuard path="/geofenceMap">
//               <GeofenceMap />
//             </ModuleGuard>
//           }
//         />
//         <Route
//           path="/logrecords"
//           element={
//             <ModuleGuard path="/logrecords">
//               <EmployeeLogRecords />
//             </ModuleGuard>
//           }
//         />
//         <Route path="/notifications" element={<Notifications />} />
//         <Route
//           path="/attendance"
//           element={
//             <ModuleGuard path="/attendance">
//               <Attendance />
//             </ModuleGuard>
//           }
//         />
//         <Route
//           path="/premises"
//           element={
//             <ModuleGuard path="/premises">
//               <EmployeePremises />
//             </ModuleGuard>
//           }
//         />
//         <Route
//           path="/sos"
//           element={
//             <ModuleGuard path="/sos">
//               <SOS />
//             </ModuleGuard>
//           }
//         />
//         <Route path="/signout" element={<SignOut />} />
//         <Route path="/cvd" element={<EmployeeCvd />} />
//         <Route path="/*" element={<EmployeeDashboard />} />
//         <Route path="/user-invitations/:invitationId" element={<AcceptUserInvitation />} />
//         {/* Invitation link — UserInvitationV2 handles its own session check internally */}
//         <Route path="/auth/invitations/:invitationId" element={<UserInvitationV2 />} />
//         <Route path="/friends" element={<FriendList />} />
//       </Route>
//     </Routes>
//   );
// };

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      refetchOnWindowFocus: false,
    },
  },
});

function Router() {
  const userQuery = useQuery('self', User.getSelf);

  const userContext = useUserContext();
  const companyRole = userContext?.company ?? { role: 'VISITOR' };

  if (userQuery.isLoading) {
    return <div style={{ color: '#fff' }}>Loading...</div>;
  }

  if (userQuery.isError) {
    return <div style={{ color: '#fff' }}>Error...</div>;
  }

  // const user = userQuery.data;

  if (companyRole.role === 'ADMIN') {
    return <AdminRouter />;
  } else {
    if ('PARTNER' === companyRole.role) {
      return <PartnerRouter />;
    }

    if (companyRole.role === 'CORPORATE_ADMIN') {
      return <CorporateRouter />;
    }

    if (companyRole.role === 'OWNER') {
      return <OwnerRouter />;
    }

    if (companyRole.role === 'MANAGER') {
      return <ManagerRouter />;
    }

    if (companyRole.role === 'VISITOR') {
      return <VisitorRouter />;
    }
    // return <EmployeeRouter />;
    return <DefaultRoleRoutes />;
  }
}

const AccessComponent = ({ children }) => {
  const userContext = useUserContext();

  const userQuery = useQuery('self', User.getSelf);
  const rolesQuery = useQuery(['companyRoles', userQuery?.data?.id], User.getRoles);

  if (userQuery.isLoading) {
    return (
      <LoadingWrapper>
        <LoadingText>
          <Spinner size="extraMedium" />
          Loading.....
        </LoadingText>
      </LoadingWrapper>
    );
  }

  if (userQuery.isError) {
    return <div style={{ color: '#fff' }}>Error...</div>;
  }

  const roles = rolesQuery?.data ?? [];
  const userDetails = userQuery?.data;
  if (userDetails) {
    userContext?.setUser(userDetails);
  }
  const permission = getObjectWithPermission(roles);
  if (!userContext?.company) {
    if ((roles?.length ?? 0) > 0) {
      const role = roles[0];

      userContext?.setCompany(role);
      if (permission) {
        userContext.setUserPermission(role?.permission);
      }
    }
  }

  return <>{children}</>;
};

const defaultTheme = light;

const SecuredApp = () => {
  const lang = useLanguage();

  const PreferredLanguage =
    lang?.selectedLang === 'English'
      ? EnglishContent
      : lang?.selectedLang === 'Portuguese'
      ? PortugueseContent
      : EnglishContent;

  return (
    <SecureComponent login={<Login />}>
      <IntlProvider locale={'pt-BR'} messages={PreferredLanguage}>
        <ThemeProvider theme={defaultTheme}>
          <ThemedGlobalStyle />
          <QueryClientProvider client={queryClient}>
            <UserProvider>
              <CompanyProvider>
                <AccessComponent>
                  <AppHolder id="app-global">
                    <Router />
                  </AppHolder>
                </AccessComponent>
              </CompanyProvider>
            </UserProvider>
          </QueryClientProvider>
        </ThemeProvider>
      </IntlProvider>
    </SecureComponent>
  );
};

function App() {
  return (
    <AuthProvider>
      <BrowserRouter>
        <LanguageProvider>
          <Routes>
            <Route path="/*" element={<SecuredApp />} />
            {/* <Route
              path="/auth/invitations/:invitationId"
              element={
                <ThemeProvider theme={defaultTheme}>
                  <ThemedGlobalStyle />
                  <AppHolder id="app-global">
                    <UserInvitation />
                  </AppHolder>
                </ThemeProvider>
              }
            /> */}
            <Route
              path="/user-invitations/:invitationId"
              element={
                <ThemeProvider theme={defaultTheme}>
                  <ThemedGlobalStyle />
                  <AppHolder id="app-global">
                    <AcceptUserInvitation />
                  </AppHolder>
                </ThemeProvider>
              }
            />
            <Route
              path="/invoice/:companyId/:userId"
              element={
                <ThemeProvider theme={defaultTheme}>
                  <ThemedGlobalStyle />
                  <AppHolder id="app-global">
                    <Invoice />
                  </AppHolder>
                </ThemeProvider>
              }
            />
            <Route path="/mymla/callback" />
          </Routes>
        </LanguageProvider>
      </BrowserRouter>
    </AuthProvider>
  );
}

export default App;
